import {CHANNELS} from "data/products";
import {RELEASES_SETS} from "data/constants";
import {DOT_NET_RELEASE_PRODUCTS} from "data/dot-net-products";
import {TEMPLATE_CATALOG, type TemplateDefinition as CatalogEntry} from "data/template-catalog";
import {type TemplateMetadata, type TemplateSlot} from "data/templateManifest";
import {
  ALL_TEMPLATE_SLOTS,
  createTemplateSlotIndex,
  slotMember,
  type TemplatePool,
  type TemplateSelection,
} from "data/templateSlots";

/**
 * The view of the Templates tab: one card per design (template key) with its
 * pool and the places it has on the pages. Pure, so the tab and its tests read
 * the same thing.
 */

export type FleetMember = {
  key: string;
  id: string;
  label: string;
  width: number;
  height: number;
  isOnAir: boolean;
  isCodeDefault: boolean;
};

export type FleetPlacement = {
  /** One place: the slot's channel and position in the channel list. */
  slotKey: string;
  channel: string;
  /** "Releases › Splash", "Ads › Ai, Deveco", "Social". */
  page: string;
  /** 1-based position among the places of the same page. */
  position: number;
  total: number;
  layouts: string;
  products: string;
  /** The designs of the place, oldest first. One for most places. */
  members: FleetMember[];
};

export type FleetCard = {
  key: string;
  id: string;
  label: string;
  width: number;
  height: number;
  dimensions: string;
  pool: TemplatePool;
  channels: string[];
  placements: FleetPlacement[];
  /** The catalog entry the preview draws: the design in its first channel. */
  preview: CatalogEntry;
  /** The text a search matches. */
  searchText: string;
};

export const POOLS: ReadonlyArray<{value: TemplatePool; label: string}> = [
  {value: "on-air", label: "On air"},
  {value: "candidate", label: "Candidates"},
  {value: "archive", label: "Archive"},
];

function channelLabel(channel: string): string {
  const found = CHANNELS.find((entry: {value: string}) => entry.value === channel);
  if (!found) throw new Error(`Unknown channel "${channel}"`);
  return found.label;
}

function setLabel(set: string): string {
  const found = RELEASES_SETS.find((entry: {value: string}) => entry.value === set);
  return found ? found.label : set;
}

/** The page of a slot: its channel, and its set or content types. */
function pageOf(template: TemplateMetadata, channel: string): {key: string; label: string} {
  const parts = [template.set, template.contentTypes?.join(",")].filter(Boolean);
  const detail = template.set
    ? setLabel(template.set)
    : template.contentTypes
      ? template.contentTypes.join(", ")
      : "";
  return {
    key: [channel, ...parts].join("/"),
    label: detail ? `${channelLabel(channel)} › ${detail}` : channelLabel(channel),
  };
}

function layoutsText(template: TemplateMetadata, channel: string): string {
  const base = template.layouts ? `layouts ${template.layouts.join(", ")}` : "every layout";
  if (channel !== "releases") return base;
  return template.uploadLayouts
    ? `${base}, also the upload layouts 5 and 6`
    : `${base} but the upload layouts 5 and 6`;
}

function sameProducts(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((code) => b.includes(code));
}

function productsText(template: TemplateMetadata): string {
  // The products whose pages draw the banner (the .NET splashes), then the
  // products that see the design under its id (Air's own emails).
  if (template.drawnFor) {
    return sameProducts(template.drawnFor, DOT_NET_RELEASE_PRODUCTS)
      ? "the .NET products only"
      : `only ${template.drawnFor.join(", ")}`;
  }
  if (template.notDrawnFor) {
    return sameProducts(template.notDrawnFor, DOT_NET_RELEASE_PRODUCTS)
      ? "every product but the .NET ones"
      : `every product but ${template.notDrawnFor.join(", ")}`;
  }
  if (template.products) {
    return sameProducts(template.products, DOT_NET_RELEASE_PRODUCTS)
      ? "the .NET products only"
      : `only ${template.products.join(", ")}`;
  }
  if (template.exceptProducts) {
    return sameProducts(template.exceptProducts, DOT_NET_RELEASE_PRODUCTS)
      ? "every product but the .NET ones"
      : `every product but ${template.exceptProducts.join(", ")}`;
  }
  return "every product";
}

function placementOf(
  slot: TemplateSlot,
  slots: readonly TemplateSlot[],
  selection: TemplateSelection
): FleetPlacement {
  const first = slot.members[0];
  const page = pageOf(first, slot.channel);
  const samePage = slots.filter(
    (other) => other.channel === slot.channel && pageOf(other.members[0], other.channel).key === page.key
  );
  const drawn = slotMember(slot, selection);
  return {
    slotKey: `${slot.channel}:${slot.index}`,
    channel: slot.channel,
    page: page.label,
    position: samePage.indexOf(slot) + 1,
    total: samePage.length,
    layouts: layoutsText(first, slot.channel),
    products: productsText(first),
    members: slot.members.map((member) => ({
      key: member.key,
      id: member.id,
      label: member.label,
      width: member.width,
      height: member.height,
      isOnAir: member === drawn,
      isCodeDefault: member === slot.defaultMember,
    })),
  };
}

/** Every design as a card, in the order the channels list them. */
export function buildFleetCards(
  selection: TemplateSelection,
  slots: readonly TemplateSlot[] = ALL_TEMPLATE_SLOTS
): FleetCard[] {
  const byChannel: Record<string, TemplateSlot[]> = {};
  for (const slot of slots) (byChannel[slot.channel] ||= []).push(slot);
  const pools = createTemplateSlotIndex(byChannel).templatePools(selection);
  const cards = new Map<string, FleetCard>();
  for (const slot of slots) {
    for (const member of slot.members) {
      const existing = cards.get(member.key);
      const placement = placementOf(slot, slots, selection);
      if (existing) {
        existing.placements.push(placement);
        if (!existing.channels.includes(slot.channel)) existing.channels.push(slot.channel);
        continue;
      }
      const preview = TEMPLATE_CATALOG.find(
        (entry) => entry.channel === slot.channel && entry.key === member.key
      );
      if (!preview) throw new Error(`Template "${member.key}" has no catalog entry`);
      const pool = pools.get(member.key);
      if (!pool) throw new Error(`Template "${member.key}" has no pool`);
      cards.set(member.key, {
        key: member.key,
        id: member.id,
        label: member.label,
        width: member.width,
        height: member.height,
        dimensions: member.dimensions,
        pool,
        channels: [slot.channel],
        placements: [placement],
        preview,
        searchText: "",
      });
    }
  }
  return [...cards.values()].map((card) => ({
    ...card,
    searchText: [
      card.label,
      card.id,
      card.key,
      card.dimensions,
      `${card.width}x${card.height}`,
      ...card.placements.map((placement) => placement.page),
    ]
      .join(" ")
      .toLowerCase(),
  }));
}

export type FleetFilter = {pool: TemplatePool; channel: string | null; search: string};

/** The cards a filter shows. A channel filter keeps a card with a place on it. */
export function filterFleetCards(cards: readonly FleetCard[], filter: FleetFilter): FleetCard[] {
  const words = filter.search.toLowerCase().split(/\s+/).filter(Boolean);
  return cards.filter(
    (card) =>
      card.pool === filter.pool &&
      (!filter.channel || card.channels.includes(filter.channel)) &&
      words.every((word) => card.searchText.includes(word))
  );
}

/** The number of cards in each pool, for the tab labels. */
export function poolCounts(cards: readonly FleetCard[]): Record<TemplatePool, number> {
  const counts: Record<TemplatePool, number> = {"on-air": 0, candidate: 0, archive: 0};
  for (const card of cards) counts[card.pool] += 1;
  return counts;
}
